import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  buildMasterPayload,
  formStateOf,
  validateMasterForm,
} from "./dentalWorkspaceMasterForm";

const DentalWorkspaceMasterModal = ({ open, onClose, data, api, noun }) => {
  const rowId = data?._id;
  const isUpdate = Boolean(rowId);

  const [form, setForm] = useState(() => formStateOf(data || {}));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(formStateOf(data || {}));
    setTouched(false);
  }, [open, data]);

  const validationError = useMemo(() => validateMasterForm(form), [form]);

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async () => {
    setTouched(true);
    if (validationError) return;

    const payload = buildMasterPayload(form);
    if (isUpdate && typeof data?.isActive === "boolean") payload.isActive = data.isActive;

    setSaving(true);
    try {
      if (isUpdate) {
        await api.update(rowId, payload);
        toast.success(`${noun} updated successfully`);
      } else {
        await api.create(payload);
        toast.success(`${noun} created successfully`);
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} ${noun.toLowerCase()}`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose("close")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? `Edit ${noun}` : `Add ${noun}`}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Diagnosis name"
            required
            fullWidth
            size="small"
            value={form.name}
            onChange={setField("name")}
            inputProps={{ maxLength: 300, "data-testid": "diagnosis-name" }}
          />
          <TextField
            label="Category"
            fullWidth
            size="small"
            value={form.category}
            onChange={setField("category")}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Site type"
            fullWidth
            size="small"
            value={form.siteType}
            onChange={setField("siteType")}
            inputProps={{ maxLength: 200 }}
          />

          {touched && validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DentalWorkspaceMasterModal;
